// /////////////////////////////////////////////////////////////
// [ファイル] CrmCustomerLayout.tsx
// [概要] /crm/customers 配下の親 Layout。
//        useCrmTable / useBuildingTable をここで一度だけ呼び、子ルート
//        (一覧 / 顧客詳細 / 建物詳細) に Context 経由で共有する。子ルートが
//        切り替わっても本 Layout はマウントされたままなので、データ配列・page・
//        検索条件・スクロール対象データは保持される。
//        『建物住所』タブの選択状態 (isBuildingTab) も本 Layout が単一の真実として持つ。
// /////////////////////////////////////////////////////////////
import React, { useRef, useState } from 'react';
import { useLocation, useOutlet, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useCrmTable } from '../hooks/useCrmTable';
import { useBuildingTable } from '../hooks/useBuildingTable';
import { CrmTableContext } from '../providers/CrmTableProvider';

export default function CrmCustomerLayout() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const outlet = useOutlet();

  // 初回マウント時の URL Query (?tab=...) を初期フィルターへ反映
  // タブ切り替え後は updateFilters() 経由で内部 state が更新される
  const initialTab = searchParams.get('tab');
  const tableState = useCrmTable({
    customer_type:
      initialTab === 'individual' || initialTab === 'corporate' ? initialTab : '',
  });

  // 『建物住所』タブの選択状態。建物一覧は初めてタブが開かれるまでフェッチしない
  const [isBuildingTab, setIsBuildingTab] = useState(initialTab === 'building');
  const buildingState = useBuildingTable(isBuildingTab);

  // 一覧テーブルのスクロール位置。一覧 → 詳細 → 戻る で復元するために
  // ref として保持する (state にすると Layout が再レンダーされる)。
  // 顧客一覧と建物一覧は行構成が異なるため、それぞれ別の ref で保持する。
  const scrollPositionRef = useRef<number>(0);
  const buildingScrollPositionRef = useRef<number>(0);

  return (
    <CrmTableContext.Provider
      value={{
        ...tableState,
        scrollPositionRef,
        building: { ...buildingState, scrollPositionRef: buildingScrollPositionRef },
        isBuildingTab,
        setIsBuildingTab,
      }}
    >
      {/* 一覧 ↔ 詳細 の切り替え時にフェード＆スライドのトランジションを適用。
          Layout 自体はマウントされ続けるので、Context 配下の状態
          (データ・page・検索条件・スクロール位置) は保持される。 */}
      <div style={{ overflow: 'hidden' }}>
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            {outlet}
          </motion.div>
        </AnimatePresence>
      </div>
    </CrmTableContext.Provider>
  );
}
